import { useState, useEffect, useRef } from "react";
import { storageKey, normalize } from "../utils/tourUtils";
import { saveTourProgressHybrid, loadTourProgressHybrid } from "../userDataSync";

/**
 * Custom hook for managing tour progress
 * Handles localStorage persistence, answer validation, and step navigation
 */
export function useProgress(slug, steps) {
  const [progress, setProgress] = useState(() => {
    try {
      const key = storageKey(slug);
      const raw = localStorage.getItem(key);
      if (raw) {
        const data = JSON.parse(raw);
        // Ensure new fields exist for backward compatibility
        return {
          ...data,
          wrongAttempts: data.wrongAttempts || {},
          streak: data.streak || 0
        };
      }

      // Legacy migration: look for old storage keys
      const legacyCandidates = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (!k) continue;
        if (k.startsWith('tib_progress_') && k !== key) legacyCandidates.push(k);
      }

      const preferred = legacyCandidates.find(k => /san_juan_v1|san-juan|cofresi|tib_progress/i.test(k)) || legacyCandidates[0];
      if (preferred) {
        const rawLegacy = localStorage.getItem(preferred);
        if (rawLegacy) {
          try {
            const data = JSON.parse(rawLegacy);
            const migrated = {
              ...data,
              wrongAttempts: data.wrongAttempts || {},
              streak: data.streak || 0
            };
            localStorage.setItem(key, JSON.stringify(migrated));
            return migrated;
          } catch {}
        }
      }
      return { index: 0, reveals: {}, answers: {}, letterHints: {}, wrongAttempts: {}, streak: 0 };
    } catch {
      return { index: 0, reveals: {}, answers: {}, letterHints: {}, wrongAttempts: {}, streak: 0 };
    }
  });

  // Timer for debounced Firebase saves (60 seconds)
  const firebaseSaveTimerRef = useRef(null);

  // Load progress from Firebase on mount (if user is logged in)
  useEffect(() => {
    const loadFromFirebase = async () => {
      try {
        // Lazy import to avoid initialization issues
        const { auth } = await import("../firebaseConfig");
        const user = auth.currentUser;
        if (user) {
          const firebaseProgress = await loadTourProgressHybrid(user.uid, slug);
          if (firebaseProgress && Object.keys(firebaseProgress).length > 0) {
            // Firebase has data, use it
            setProgress({
              ...firebaseProgress,
              wrongAttempts: firebaseProgress.wrongAttempts || {},
              streak: firebaseProgress.streak || 0
            });
          }
        }
      } catch (e) {
        console.warn('Failed to load progress from Firebase', e);
      }
    };

    loadFromFirebase();
  }, [slug]);

  const clamp = (i) => Math.max(0, Math.min(i, Math.max(0, steps.length - 1)));
  const current = steps[clamp(progress.index)] || {
    id: "placeholder",
    title: "Loading…",
    directions: "",
    riddle: "",
    hint: "",
    history: "",
    nearby: [],
    answerMeta: "",
    acceptedAnswers: [""],
    image: "",
  };

  const reveal = (stepId, section) => {
    setProgress((p) => ({
      ...p,
      reveals: { ...p.reveals, [stepId]: { ...(p.reveals?.[stepId] || {}), [section]: true } },
    }));
  };

  const isRevealed = (stepId, section) => !!progress.reveals?.[stepId]?.[section];

  const submitAnswer = (stepId, userAnswer) => {
    const normalizedAnswer = normalize(userAnswer);
    const step = steps.find(s => s.id === stepId);

    if (!step) {
      setProgress((p) => ({ ...p, answers: { ...p.answers, [stepId]: normalizedAnswer } }));
      return;
    }

    const isCorrect = Array.isArray(step.acceptedAnswers) && step.acceptedAnswers.includes(normalizedAnswer);

    setProgress((p) => {
      // Ensure wrongAttempts exists
      const currentWrongAttempts = p.wrongAttempts || {};
      const hasWrongAttempt = !!currentWrongAttempts[stepId];
      const currentStreak = p.streak || 0;

      let newStreak = currentStreak;
      let newWrongAttempts = currentWrongAttempts;

      if (isCorrect) {
        // Correct answer
        if (!hasWrongAttempt) {
          // First try correct - increment streak
          newStreak = currentStreak + 1;
        }
        // If hasWrongAttempt, streak stays the same (don't increment)
      } else {
        // Wrong answer - mark wrong attempt and reset streak to 0
        newWrongAttempts = { ...currentWrongAttempts, [stepId]: true };
        newStreak = 0;
      }

      return {
        ...p,
        answers: { ...p.answers, [stepId]: normalizedAnswer },
        wrongAttempts: newWrongAttempts,
        streak: newStreak
      };
    });
  };

  const isAnsweredCorrect = (step) => {
    const a = progress.answers?.[step.id];
    if (!a) return undefined;
    // Normalize both the stored answer and accepted answers for comparison
    const normalizedAnswer = normalize(a);
    return Array.isArray(step.acceptedAnswers) && step.acceptedAnswers.some(
      (accepted) => normalize(accepted) === normalizedAnswer
    );
  };

  const getLetterMask = (step) => {
    try {
      const targetRaw = (step?.solution || (step?.acceptedAnswers?.[0] ?? "")).toString().toUpperCase().trim();
      const target = targetRaw.replace(/\s+/g, "");
      const len = target.length || 1;
      const saved = (progress.letterHints?.[step.id] ?? []).slice(0, len);
      while (saved.length < len) saved.push(false);
      return saved;
    } catch {
      return [];
    }
  };

  const revealNextLetter = (step) => {
    try {
      const mask = getLetterMask(step);
      const idx = mask.findIndex((v) => !v);
      if (idx === -1) return;
      const nextMask = mask.slice();
      nextMask[idx] = true;
      setProgress((p) => ({
        ...p,
        letterHints: { ...(p.letterHints || {}), [step.id]: nextMask },
      }));
    } catch {}
  };

  const goto = (idx) => setProgress((p) => ({ ...p, index: clamp(idx) }));
  const next = () => goto(progress.index + 1);
  const prev = () => goto(progress.index - 1);
  const reset = () => setProgress({ index: 0, reveals: {}, answers: {}, letterHints: {}, wrongAttempts: {}, streak: 0 });

  // Persist to localStorage (instant) and Firebase (debounced) on every change
  useEffect(() => {
    console.log('[useProgress] Progress changed, saving to localStorage...', slug, progress);

    // Always save to localStorage immediately (instant feedback)
    try {
      const key = storageKey(slug);
      localStorage.setItem(key, JSON.stringify(progress));
      console.log('[useProgress] ✅ Saved to localStorage:', key);
    } catch (e) {
      console.error('[useProgress] ❌ Failed to save to localStorage', e);
    }

    // Debounced Firebase save (60 seconds) to reduce writes
    if (firebaseSaveTimerRef.current) {
      clearTimeout(firebaseSaveTimerRef.current);
      console.log('[useProgress] Cleared previous Firebase save timer');
    }

    console.log('[useProgress] Scheduling Firebase save in 60 seconds...');
    firebaseSaveTimerRef.current = setTimeout(async () => {
      try {
        const { auth } = await import("../firebaseConfig");
        const user = auth.currentUser;
        console.log('[useProgress] Debounce timer fired. User logged in?', !!user, 'UserID:', user?.uid);
        if (user) {
          console.log('[useProgress] 🔄 Saving to Firebase (debounced):', slug, progress);
          await saveTourProgressHybrid(user.uid, slug, progress);
          console.log('[useProgress] ✅ Saved to Firebase successfully');
        } else {
          console.warn('[useProgress] ⚠️ No user logged in, skipping Firebase save');
        }
      } catch (e) {
        console.error('[useProgress] ❌ Failed to save to Firebase', e);
      }
    }, 60000); // 60 seconds

    // Cleanup: Save to Firebase immediately on unmount (don't lose progress)
    return () => {
      if (firebaseSaveTimerRef.current) {
        clearTimeout(firebaseSaveTimerRef.current);
      }

      // Immediate save on unmount
      (async () => {
        try {
          const { auth } = await import("../firebaseConfig");
          const user = auth.currentUser;
          if (user) {
            console.log('[useProgress] 🔄 Saving to Firebase (unmount):', slug);
            await saveTourProgressHybrid(user.uid, slug, progress);
            console.log('[useProgress] ✅ Saved to Firebase on unmount');
          }
        } catch (e) {
          console.error('[useProgress] ❌ Failed to save to Firebase on unmount', e);
        }
      })();
    };
  }, [slug, progress]);

  return {
    progress,
    current,
    isRevealed,
    reveal,
    submitAnswer,
    isAnsweredCorrect,
    next,
    prev,
    goto,
    reset,
    getLetterMask,
    revealNextLetter,
    streak: progress.streak || 0
  };
}
